import styled from "styled-components";
import { useState, useContext, useEffect, useRef } from "react";
import { writeToHotTopicsDatabase, deleteHotTopic } from "../../../Firebase";
import { OverlayPanel } from "primereact/overlaypanel";
import { allFirstLetterCap } from "../../../Helpers/StringFormat";
import { HotTopicsContext } from "../../../Contexts/HotTopicsContext";
import { DialogWindowContext } from "../../../Contexts/DialogueWindowContext";
import { CurrentUserContext } from "../../../Contexts/CurrentUserContext";
import { Chip } from "primereact/chip";
import { Tooltip } from "primereact/tooltip";
import HotTopicEdit from "./HotTopicEdit";

const HotTopicsAdminWrapper = styled.div`
  padding: 10px;
  h2 {
    color: var(--accent-color-dark);
    font-size: 25px;
    font-weight: 500;
  }
`;

const HotTopicsWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  border: 1px solid var(--accent-color-dark);
  min-height: 100px;
  padding: 10px;
  border-radius: 7px;
  box-shadow: inset 0px 0px 3px 0px rgba(0, 0, 0, 0.75);

  .bucket {
    display: flex;
    width: 100%;
  }

  h3 {
    color: var(--accent-color-dark);
    font-size: 15px;
    font-weight: 300;
    margin-bottom: 10px;
    margin-right: 6px;
  }

  .p-chip {
    width: fit-content;
    background-color: var(--accent-color-dark);
    color: white;
    margin: 5px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.75);
    transition: 0.2s;
  }
  .p-chip:hover {
    box-shadow: 0px 0px 4px 1px rgba(0, 0, 0, 0.75);
  }

  .new-hot-topic {
    background-color: green;
  }
`;

const Tag = styled.div`
  display: flex;
  justify-items: center;
  align-items: center;
  padding: 3px 7px;
  background-color: #b7b7b7;
  border-radius: 10px;
  color: white;
  width: fit-content;
  height: fit-content;

  h4 {
    font-size: 12px;
    text-transform: capitalize;
  }
  i {
    font-size: 12px;
    margin-left: 5px;
    cursor: pointer;
  }
`;

export default function HotTopicsAdmin(props: Props) {
  const hotTopics = useContext(HotTopicsContext);
  const DialogWindow = useContext(DialogWindowContext);
  const currentUser = useContext(CurrentUserContext);

  const classifiedHotTopics = hotTopics.filter(
    (hotTopic) => hotTopic.status === "classified"
  );

  const unclassifiedHotTopics = hotTopics.filter(
    (hotTopic) => hotTopic.status === "unclassified"
  );

  const ignoredHotTopics = hotTopics.filter(
    (hotTopic) => hotTopic.status === "ignored"
  );

  const showEditHotTopicHandler = (label: string) => {
    DialogWindow.setDialogProps((prevProps) => ({
      ...prevProps,
      showEditWindow: true,
      header: `${label}`,
      children: <HotTopicEdit label={label} />,
    }));
  };

  useEffect(() => {
    unclassifiedHotTopics.forEach((hotTopic) => {
      const matches: string[] = [];
      classifiedHotTopics.forEach((hotTopicFromContext) => {
        if (
          hotTopicFromContext.label.trim().toLowerCase() ===
          hotTopic.label.trim().toLowerCase()
        ) {
          matches.push(hotTopicFromContext.label.trim().toLocaleLowerCase());
        }
        if (
          hotTopicFromContext.keyWords?.some(
            (keyWord) =>
              keyWord.trim().toLowerCase() ==
              hotTopic.label.trim().toLowerCase()
          )
        ) {
          matches.push(hotTopicFromContext.label.trim().toLowerCase());
        }
      });
      if (matches.length > 0) {
        deleteHotTopic(hotTopic.hotTopicId!);
      }
    });
  }, [hotTopics]);

  function ignoreHotTopic(hotTopic: HotTopic) {
    writeToHotTopicsDatabase({
      ...hotTopic,
      status: "ignored",
    });
  }
  return (
    <HotTopicsAdminWrapper>
      <h2>Hot Topics Admin</h2>

      <HotTopicsWrapper>
        <div className="bucket">
          <h3>Loose Hot Topics</h3>
          <i
            className="pi pi-info-circle non-ategorized"
            data-pr-tooltip="These Hot Topics are suggestions from submitted report. Add them to the Hot Topics or to a Hot Topic's key words."
          ></i>
          <Tooltip target=".non-ategorized" />
        </div>
        {hotTopics.map((hotTopic, index) => {
          if (hotTopic.status == "unclassified")
            return (
              <Tag key={index}>
                <h4>{allFirstLetterCap(hotTopic.label)}</h4>
                <i
                  className="pi pi-times-circle"
                  onClick={() => ignoreHotTopic(hotTopic)}
                ></i>
              </Tag>
            );
        })}
      </HotTopicsWrapper>
      <HotTopicsWrapper>
        <div className="bucket">
          <h3>Categorized Hot Topics</h3>
          <i
            className="pi pi-info-circle categorized"
            data-pr-tooltip="These Hot Topics will appear as a suggestion and are accounted for in the main Hot Topics Report."
          ></i>
          <Tooltip target=".categorized" />
        </div>
        <Chip
          className="new-hot-topic"
          label="New Hot Topic"
          onClick={() => showEditHotTopicHandler("New Hot Topic")}
        />
        {hotTopics.map((hotTopic) => {
          if (hotTopic.status == "classified")
            return (
              <Chip
                label={allFirstLetterCap(hotTopic.label)}
                onClick={() => showEditHotTopicHandler(hotTopic.label)}
              />
            );
        })}
      </HotTopicsWrapper>
      <HotTopicsWrapper>
        <div className="bucket">
          <h3>Ignored Hot Topics</h3>
          <i
            className="pi pi-info-circle non-ategorized"
            data-pr-tooltip="These Hot Topics are ignored from the System because they are unclassifiable, irrelevant or too broad."
          ></i>
          <Tooltip target=".non-ategorized" />
        </div>
        {hotTopics.map((hotTopic, index) => {
          if (hotTopic.status == "ignored")
            return (
              <Tag key={index}>
                <h4>{allFirstLetterCap(hotTopic.label)}</h4>
                {currentUser?.currentFirebaseUser?.uid ===
                  "3WJ1udT18chFjNcO5OzMhabEAKS2" && (
                  <i
                    className="pi pi-times-circle"
                    onClick={() => {
                      window.confirm(
                        "Are you sure you want to delete this hot topic? If you delete it any time someone uses it won't be accounted for."
                      ) && deleteHotTopic(hotTopic.hotTopicId!);
                    }}
                  ></i>
                )}
              </Tag>
            );
        })}
      </HotTopicsWrapper>
    </HotTopicsAdminWrapper>
  );
}

interface Props {}
